import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { isConfigurableClaimBarShown } from '~/components/ClaimPage/claimPageVariant';
import { useCms } from '~/components/hooks/useCms';
import type { ResolvedClaimFieldsResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

export const getResolvedClaimBarUrl = (organizationId: number, claimId: number): string =>
  `/api/v1/organizations/${organizationId}/claim_fields_configuration/claims/${claimId}/resolved`;

// A request that hangs is treated as failed, so the bar reaches the Default as a failed one does rather
// than wait on an empty strip; the request is aborted, so a late answer cannot replace that bar. The app
// sets no axios timeout anywhere to follow. Ten seconds sits above the production p99.9 of loading the same
// claim (the claim GET this request repeats), so a slow request that would succeed is rarely cut off.
const RESOLVED_CLAIM_BAR_REQUEST_CONFIG = { timeout: 10_000 };

type ResolvedClaimBarState = {
  data?: ResolvedClaimFieldsResponse;
  isError: boolean;
};

type ResolvedClaimBarFetch = ResolvedClaimBarState & { reload: () => void };

export const ResolvedClaimBarContext = createContext<ResolvedClaimBarFetch | null>(null);

type ResolvedClaimBarProviderProps = {
  claimId: number;
  children: React.ReactNode;
};

/**
 * Fetches the claim bar as the claim resolves it from the route's claim id, in parallel with the claim
 * itself: the bar mounts only once the claim has loaded, and the request needs nothing from it.
 *
 * The provider outlives the bar, which the claim container unmounts on every claim change, so it owns the
 * ordering: only the newest request's answer is applied. An earlier request - the first fetch, a reload
 * for a claim update, or one for the previous claim - can never land last.
 */
export const ResolvedClaimBarProvider = ({ claimId, children }: ResolvedClaimBarProviderProps) => {
  const { userOrganization } = useCms();
  // Only the configurable claim bar reads this. Where it is not shown the provider holds nothing, so a
  // bar that mounts anyway shows the Default. The route parses its id with Number(), so a mistyped URL
  // arrives as NaN: the claim container shows its not-found state, and nothing is asked of the server.
  const isClaimBarShown = isConfigurableClaimBarShown(userOrganization) && Number.isFinite(claimId);
  const url = getResolvedClaimBarUrl(userOrganization.id, claimId);
  const [state, setState] = useState<ResolvedClaimBarState>({ isError: false });
  const latestRequest = useRef(0);

  const reload = useCallback(async () => {
    const request = ++latestRequest.current;
    try {
      const response = await axios.get<ResolvedClaimFieldsResponse>(url, RESOLVED_CLAIM_BAR_REQUEST_CONFIG);
      if (request === latestRequest.current) setState({ data: response.data, isError: false });
    } catch (error) {
      // Reported without the modal dialog: nobody asked for this fetch, and the bar still shows the Default.
      reportAxiosError(error, false);
      if (request === latestRequest.current) setState((current) => ({ ...current, isError: true }));
    }
  }, [url]);

  useEffect(() => {
    if (!isClaimBarShown) return;
    // An error left from the previous claim must not send this one's bar straight to the Default.
    setState((current) => ({ ...current, isError: false }));
    reload();
  }, [isClaimBarShown, reload]);

  const value = useMemo(() => ({ ...state, reload }), [state, reload]);

  return (
    <ResolvedClaimBarContext.Provider value={isClaimBarShown ? value : null}>
      {children}
    </ResolvedClaimBarContext.Provider>
  );
};
